import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronRight, Copy, Plus } from 'lucide-react';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { apiFetch } from '../../lib/api';
import {
  STARTER_CATEGORIES,
  STARTER_PROMPTS,
  type StarterCategory,
  type StarterPrompt,
} from '../../lib/aiStarterPrompts';
import { cn } from '../../lib/utils';
import type { AiPrompt } from '../../types/ai';

interface PromptInput {
  title: string;
  body: string;
  shared: boolean;
}

/** Small row actions: the primary one filled, the rest quiet. */
const ROW_BUTTON = 'h-8 px-2.5 text-xs';
const ICON_BUTTON =
  'grid h-8 w-8 shrink-0 place-items-center rounded-[8px] text-muted hover:bg-bg hover:text-text';

function StarterPrompts({ onUse, canUse }: { onUse: (text: string) => void; canUse: boolean }) {
  const [category, setCategory] = useState<StarterCategory | 'All'>('All');
  const [copied, setCopied] = useState<{ id: string; ok: boolean } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  async function copy(p: StarterPrompt) {
    try {
      await navigator.clipboard.writeText(p.body);
      setCopied({ id: p.id, ok: true });
    } catch {
      setCopied({ id: p.id, ok: false });
      // Show the text so it can be selected by hand.
      setOpenId(p.id);
    }
  }

  const shown = STARTER_PROMPTS.filter((p) => category === 'All' || p.category === category);

  return (
    <div className="flex flex-col gap-2">
      <div>
        <h3 className="m-0 text-sm font-semibold">Starter prompts</h3>
        <p className="m-0 text-xs text-muted">Replace the [brackets] with your own details.</p>
      </div>
      <div role="group" aria-label="Prompt category" className="flex flex-wrap gap-1.5 py-1">
        {(['All', ...STARTER_CATEGORIES] as const).map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={category === c}
            onClick={() => setCategory(c)}
            className={cn(
              'h-7 shrink-0 rounded-full border px-2.5 text-xs transition-colors',
              category === c
                ? 'border-accent bg-accent-soft font-medium text-link'
                : 'border-border text-muted hover:border-zinc-300 hover:text-text',
            )}
          >
            {c}
          </button>
        ))}
      </div>
      <ul className="m-0 flex list-none flex-col divide-y divide-border-soft border-y border-border-soft p-0">
        {shown.map((p) => {
          const expanded = openId === p.id;
          const bodyId = `ai-starter-${p.id}`;
          return (
            <li key={p.id} className="py-2.5">
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={bodyId}
                  onClick={() => setOpenId(expanded ? null : p.id)}
                  className="group flex min-w-0 flex-1 items-start gap-1.5 rounded-[6px] text-left"
                >
                  <ChevronRight
                    size={14}
                    aria-hidden="true"
                    className={cn(
                      'mt-[3px] shrink-0 text-muted transition-transform',
                      expanded && 'rotate-90',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium group-hover:text-link">
                      {p.title}
                    </span>
                    <span className="block text-xs text-muted">{p.description}</span>
                  </span>
                </button>
                <div className="flex shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    aria-label={`Copy prompt ${p.title}`}
                    title="Copy"
                    onClick={() => void copy(p)}
                    className={ICON_BUTTON}
                  >
                    {copied?.id === p.id && copied.ok ? (
                      <Check size={15} aria-hidden="true" className="text-success-text" />
                    ) : (
                      <Copy size={15} aria-hidden="true" />
                    )}
                  </button>
                  {canUse ? (
                    <Button
                      size="sm"
                      className={ROW_BUTTON}
                      aria-label={`Use prompt ${p.title}`}
                      onClick={() => onUse(p.body)}
                    >
                      Use
                    </Button>
                  ) : null}
                </div>
              </div>
              {copied?.id === p.id ? (
                <p role="status" className="m-0 mt-1 pl-5 text-xs text-muted">
                  {copied.ok ? 'Copied' : 'Could not copy. Select the text below.'}
                </p>
              ) : null}
              <p
                id={bodyId}
                hidden={!expanded}
                className="m-0 ml-5 mt-2 whitespace-pre-wrap rounded-[8px] bg-bg px-3 py-2 text-xs text-muted"
              >
                {p.body}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Starter prompts for everyone, plus saved prompts per company (K7). "Shared" prompts are visible to everyone in
 * the company; only the author or an admin changes or deletes one. It is just
 * saved text, so it works while the assistant itself is off.
 */
export function AiPromptLibrary({
  onUse,
  canUse,
}: {
  onUse: (text: string) => void;
  /** False while the assistant is off: there is no chat to put the text in. */
  canUse: boolean;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-prompts'],
    queryFn: () => apiFetch<{ prompts: AiPrompt[] }>('/ai/prompts'),
  });
  const [editing, setEditing] = useState<AiPrompt | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [shared, setShared] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ai-prompts'] });
  const reset = () => {
    setEditing(null);
    setTitle('');
    setBody('');
    setShared(false);
    setFormOpen(false);
  };

  const save = useMutation({
    mutationFn: (input: PromptInput): Promise<unknown> =>
      editing
        ? apiFetch<{ ok: true }>(`/ai/prompts/${editing.id}`, { method: 'PUT', body: input })
        : apiFetch<{ prompt: AiPrompt }>('/ai/prompts', { method: 'POST', body: input }),
    onSuccess: () => {
      reset();
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: true }>(`/ai/prompts/${id}`, { method: 'DELETE' }),
    onSuccess: () => void refresh(),
  });

  function edit(p: AiPrompt) {
    setEditing(p);
    setTitle(p.title);
    setBody(p.body);
    setShared(p.shared);
    setFormOpen(true);
  }

  const prompts = data?.prompts ?? [];
  const valid = title.trim() !== '' && body.trim() !== '';

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="flex min-h-8 items-center justify-between gap-2">
          <h3 className="m-0 text-sm font-semibold">Your prompts</h3>
          {!formOpen ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-1 px-2 text-xs text-link"
              onClick={() => setFormOpen(true)}
            >
              <Plus size={14} aria-hidden="true" />
              New prompt
            </Button>
          ) : null}
        </div>
        {isLoading ? (
          <p role="status" className="m-0 text-sm text-muted">
            Loading prompts…
          </p>
        ) : null}
        {isError ? (
          <Alert className="px-3 py-2">Could not load your prompts. Try again later.</Alert>
        ) : null}
        {!isLoading && !isError && prompts.length === 0 && !formOpen ? (
          <p className="m-0 text-sm text-muted">No saved prompts yet.</p>
        ) : null}
        {prompts.length > 0 ? (
          <ul className="m-0 flex list-none flex-col divide-y divide-border-soft border-y border-border-soft p-0">
            {prompts.map((p) => (
              <li key={p.id} className="flex flex-col gap-1 py-2.5">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="block break-words text-sm font-medium">{p.title}</span>
                    <span className="block text-xs text-muted">
                      {p.shared ? 'Shared' : 'Only you'}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    {p.mine ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={ROW_BUTTON}
                          aria-label={`Edit prompt ${p.title}`}
                          onClick={() => edit(p)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={cn(ROW_BUTTON, 'text-danger-text hover:bg-danger-bg')}
                          aria-label={`Delete prompt ${p.title}`}
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(p.id)}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                    {canUse ? (
                      <Button
                        size="sm"
                        className={ROW_BUTTON}
                        aria-label={`Use prompt ${p.title}`}
                        onClick={() => onUse(p.body)}
                      >
                        Use
                      </Button>
                    ) : null}
                  </div>
                </div>
                <p className="m-0 line-clamp-3 whitespace-pre-wrap break-words text-xs text-muted">
                  {p.body}
                </p>
              </li>
            ))}
          </ul>
        ) : null}
        {remove.isError ? <Alert>Could not delete the prompt. Try again.</Alert> : null}

        {formOpen ? (
          <div className="flex flex-col gap-3 rounded-[10px] border border-border p-3">
            <h4 className="m-0 text-sm font-semibold">
              {editing ? 'Edit prompt' : 'Save a new prompt'}
            </h4>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ai-prompt-title">Prompt title</Label>
              <Input
                id="ai-prompt-title"
                value={title}
                maxLength={80}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ai-prompt-body">Prompt text</Label>
              <textarea
                id="ai-prompt-body"
                className="min-h-[72px] w-full rounded-[9px] border border-zinc-300 px-3 py-2 text-sm"
                value={body}
                maxLength={2000}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={shared}
                onChange={(e) => setShared(e.target.checked)}
              />
              Share with everyone in the company
            </label>
            {save.isError ? (
              <Alert>
                {save.error instanceof Error ? save.error.message : 'Could not save the prompt.'}
              </Alert>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={reset}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!valid || save.isPending}
                onClick={() => save.mutate({ title, body, shared })}
              >
                {editing ? 'Save prompt' : 'Add prompt'}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      <StarterPrompts onUse={onUse} canUse={canUse} />
    </div>
  );
}
